import React, {useCallback, useEffect, useState} from 'react';


import {FormInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {SmartSection} from 'src/components/smart-section';
import {CustomValidatedControl} from 'src/contexts/validation-context';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {useFormatInputValue} from 'src/hooks/useFormatInputValue';
import {VALIDATE_CONTROL_IDS} from 'src/modules/profile/account/create-user/constants';

import Button from '@pb/kit-component-button';
import {Dialog, DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import FormSection from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import {Info} from '@pb/kit-component-info';
import Label from '@pb/kit-component-label';

import {useUserDetailsValidation} from '../../useUserDetailsValidation';

import {CRMLink} from './components/crm-link.component';
import {EduPersonAffiliation} from './components/edu-person-affiliation';
import {UserDetailsSkeleton} from './user-details.skeleton';
import {UserPBId} from './user-pbid-search.component';

import type {SaveOptions} from 'src/components/save-with-reason/save-with-reason-button';
import type {UserInformation} from 'src/modules/profile/account/create-user/typedef';

const PB_ID_INFO = 'ID of the user in the PitchBook platform';
const MOVE_TO_PROFILE_MODAL_WIDTH = 500;

type Props = {
    isLoading: boolean,
    setUserDetails: React.Dispatch<React.SetStateAction<UserInformation>>,
    userDetails: UserInformation,
    userId?: number,
    disabled?: boolean,
    onSaveSfdcId: (sfdcId: string, options?: SaveOptions) => Promise<string | undefined>,
    onFieldChange: (fieldName: string) => void,
    onMoveToUserProfile: (userId: number) => void,
};

export const UserDetails = (props: Props) => {
    const {
        isLoading, userDetails, setUserDetails, userId, disabled, onSaveSfdcId, onFieldChange, onMoveToUserProfile,
    } = props;
    const {
        existedUser,
        showMoveToUserProfileModal,
        closeMoveToUserProfileModal,
        validateEmailOnChange,
        validateFirstNameOnChange,
        validateLastNameOnChange,
    } = useUserDetailsValidation();

    const [busy, setBusy] = useState(false);

    useEffect(() => {
        validateFirstNameOnChange(userDetails.firstName);
    }, [userDetails.firstName, validateFirstNameOnChange]);

    useFormatInputValue({
        value: userDetails.firstName,
        setValue: firstName => setUserDetails(state => ({...state, firstName})),
    });
    useFormatInputValue({
        value: userDetails.lastName,
        setValue: lastName => setUserDetails(state => ({...state, lastName})),
    });
    useFormatInputValue({
        value: userDetails.email,
        setValue: email => setUserDetails(state => ({...state, email})),
    });

    useEffect(() => {
        validateLastNameOnChange(userDetails.lastName);
    }, [userDetails.lastName, validateLastNameOnChange]);

    useEffectWithDeps((prev, current) => {
        const validateEmail = async () => {
            setBusy(true);
            await validateEmailOnChange(current.email, prev.userId);
            setBusy(false);
        };

        if (prev.userId === current.userId && prev.email !== current.email) {
            validateEmail();
        }
    }, {email: userDetails.email, userId});

    const onChangeJobTitle = useCallback((jobTitle: string) => {
        setUserDetails(prevState => {
            if (prevState.jobTitle !== jobTitle) {
                onFieldChange('Job title');
            }

            return {...prevState, jobTitle};
        });
    }, [setUserDetails, onFieldChange]);

    const onFirstNameChange = useCallback((firstName: string) => {
        setUserDetails(prevState => {
            if (prevState.firstName !== firstName) {
                onFieldChange('First name');
            }

            return {...prevState, firstName};
        });
    }, [setUserDetails, onFieldChange]);

    const onLastNameChange = useCallback((lastName: string) => {
        setUserDetails(prevState => {
            if (prevState.lastName !== lastName) {
                onFieldChange('Last name');
            }

            return {...prevState, lastName};
        });
    }, [setUserDetails, onFieldChange]);

    const onChangeEmail = useCallback((email: string) => {
        setUserDetails(prevState => {
            if (prevState.email !== email) {
                onFieldChange('Email');
            }

            return {...prevState, email};
        });
    }, [setUserDetails, onFieldChange]);

    const moveToUserProfile = useCallback(() => {
        if (existedUser) {
            onMoveToUserProfile(existedUser.userId);
        }
    }, [existedUser, onMoveToUserProfile]);

    return (
        <SmartSection isLoading={isLoading} hasLoadingError={false} skeletonView={<UserDetailsSkeleton/>}>
            <Dialog
                shown={showMoveToUserProfileModal}
                setShown={closeMoveToUserProfileModal}
                wrapperProps={{style: {width: MOVE_TO_PROFILE_MODAL_WIDTH}}}>
                <DialogHeader onClose={closeMoveToUserProfileModal}>
                    Open the user profile
                </DialogHeader>
                <DialogMain style={{padding: 24}}>
                    The user with this email already exists in the system.
                    Do you want to open the user's profile?
                </DialogMain>
                <DialogFooter>
                    <Group>
                        <Button variant="Type.SECONDARY" onClick={closeMoveToUserProfileModal}>Cancel</Button>
                        <Button variant="Type.PRIMARY" onClick={moveToUserProfile}>Open</Button>
                    </Group>
                </DialogFooter>
            </Dialog>
            <FormSection headline="User Details">
                <div style={{maxWidth: 1050}}>
                    <ResponsiveElementGroup>
                        <CustomValidatedControl name={VALIDATE_CONTROL_IDS.FIRST_NAME}>
                            {error => (
                                <FormInput
                                    width={300}
                                    label={<RequiredMark label="First name"/>}
                                    placeholder="First Name"
                                    readOnly={disabled}
                                    value={userDetails.firstName}
                                    maxLength={50}
                                    onBlur={onFirstNameChange}
                                    valid={error !== undefined ? false : undefined}
                                    errorDescription={error}
                                />
                            )}
                        </CustomValidatedControl>
                        <CustomValidatedControl name={VALIDATE_CONTROL_IDS.LAST_NAME}>
                            {error => (
                                <FormInput
                                    label={<RequiredMark label="Last name"/>}
                                    placeholder="Last Name"
                                    readOnly={disabled}
                                    width={300}
                                    maxLength={50}
                                    value={userDetails.lastName}
                                    onBlur={onLastNameChange}
                                    valid={error !== undefined ? false : undefined}
                                    errorDescription={error}
                                />
                            )}
                        </CustomValidatedControl>
                        <FormInput
                            width={300}
                            label="Job title"
                            placeholder="Job title"
                            value={userDetails.jobTitle || ''}
                            readOnly={disabled}
                            onBlur={onChangeJobTitle}
                            maxLength={200}
                        />
                        <CustomValidatedControl name={VALIDATE_CONTROL_IDS.EMAIL}>
                            {error => (
                                <FormInput
                                    label={<RequiredMark label="Email address"/>}
                                    readOnly={disabled}
                                    width={300}
                                    placeholder="Email address"
                                    value={userDetails.email}
                                    maxLength={255}
                                    onBlur={onChangeEmail}
                                    busy={busy}
                                    valid={error !== undefined ? false : undefined}
                                    errorDescription={error}
                                />
                            )}
                        </CustomValidatedControl>
                        <div style={{width: 300}}>
                            <Label>
                                <Info
                                    dropdownProps={{
                                        style: {
                                            width: '265px',
                                        },
                                    }}
                                    type={{popover: PB_ID_INFO}}>PB RTS ID
                                </Info>
                            </Label>
                            <UserPBId {...{
                                onFieldChange: () => onFieldChange('PB RTS ID'),
                                pbId: userDetails.pbId,
                                setUserDetails,
                                disabled,
                            }}
                            />
                        </div>
                        <EduPersonAffiliation edu={userDetails.educationAttribute}/>
                        <CRMLink {...{sfdcId: userDetails.sfdcId, editDisabled: disabled, onSave: onSaveSfdcId}}/>
                    </ResponsiveElementGroup>
                </div>
            </FormSection>
        </SmartSection>
    );
};
